import { useMutation, useQueryClient } from '@tanstack/react-query'
import { approveProcurementPurchaseRequest } from 'api'
import { AxiosError } from 'axios'
import { Badge } from 'components/Badge/Badge'
import { DotText } from 'components/Badge/DotText'
import { Card, CardBody, CardHeader } from 'components/Card/Card'
import { Spinner } from 'components/Loading/Loading'
import {
  PROCUREMENT_PRIORITY_META,
  PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META,
} from 'constants/meta/procurement'
import { ClockIconV2 } from 'pages/Accomodation/icons/ClockIconV2'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, DividerVertical, Flex, Icon, OutlineButton } from 'styling/GlobalStyles'
import { BigText, ColorVariant, MediumText, NormalText, SmallText } from 'styling/TypographyV2'
import {
  ProcurementPurchaseRequestListResponse,
  ProcurementRequestClientStatus,
  ResolveWorkflowStepRequest,
  WorkflowResolutionStatus,
  WorkflowStepType,
} from 'types/procurement'
import { convertToDate, timeAgo } from 'utils/helpers'
import { CreatePOButton } from './CreatePOButton'
import { ResolveWithCommentModal } from './ResolveWithCommentModal'

export const PurchaseRequestCard = ({
  purchaseRequest,
}: {
  purchaseRequest: ProcurementPurchaseRequestListResponse
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [commentModal, setCommentModal] = useState<WorkflowResolutionStatus | null>(null)
  const mutation = useMutation({
    mutationFn: (data: ResolveWorkflowStepRequest) =>
      approveProcurementPurchaseRequest(purchaseRequest.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procurement-purchase-request-list'] })
      queryClient.invalidateQueries({ queryKey: ['procurement-details'] })
      queryClient.invalidateQueries({ queryKey: ['procurement-purchase-request-audit-logs'] })
      queryClient.invalidateQueries({ queryKey: ['procurement-actionable-purchase-requests'] })
      toast.success('Purchase request approved successfully')
      setCommentModal(null)
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to approve purchase request')
      }
    },
  })

  const totalAmount = purchaseRequest.selectedQuote?.items.reduce(
    (acc, item) => acc + item.price * item.procurementPurchaseRequestItem?.quantity || 0,
    0,
  )
  const unresolvedFreeTextItems = purchaseRequest.items.filter((item) => !item.inventoryItem).length

  const handleRequest = (comment: string, resolutionStatus: WorkflowResolutionStatus) => {
    try {
      if (resolutionStatus !== WorkflowResolutionStatus.RequestChanges) {
        mutation.mutate({ resolutionStatus, comment })
      } else {
        if (!comment) {
          toast.error('Comment is required')
          return
        }
        const lastWorkflowStep = purchaseRequest.workflowSteps.find(
          (step) => step.order === purchaseRequest.currentWorkflowStep?.order - 1,
        )
        mutation.mutate({ resolutionStatus, comment, returnWorkflowStepId: lastWorkflowStep?.id })
      }
    } catch (error) {
      console.log(error)
      if (error instanceof AxiosError) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to approve purchase request')
      }
    }
  }

  return (
    <>
      <Spinner loading={mutation.isPending} />
      <Card>
        <CardHeader>
          <Flex between>
            <DotText hexColor={PROCUREMENT_PRIORITY_META[purchaseRequest.priority].color}>
              {PROCUREMENT_PRIORITY_META[purchaseRequest.priority].label}
            </DotText>
            <Flex center gap='0.25rem'>
              <Icon size='1.25rem' color='#B0B0B0'>
                <ClockIconV2 />
              </Icon>
              <MediumText colorVariant={ColorVariant.SECONDARY}>
                {timeAgo(purchaseRequest.createdAt)}
              </MediumText>
            </Flex>
          </Flex>
        </CardHeader>
        <CardBody>
          <Flex between>
            <Flex column>
              <Flex gap='0.5rem' center>
                <SmallText colorVariant={ColorVariant.SECONDARY}>
                  {'#PR-' +
                    new Date(convertToDate(purchaseRequest.createdAt)).getFullYear() +
                    '-' +
                    purchaseRequest.id}
                </SmallText>
                {purchaseRequest.clientStatus && (
                  <Badge
                    text={PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META[purchaseRequest.clientStatus].label}
                    red={purchaseRequest.clientStatus === ProcurementRequestClientStatus.Rejected}
                    green={
                      purchaseRequest.clientStatus === ProcurementRequestClientStatus.POCreated ||
                      purchaseRequest.clientStatus === ProcurementRequestClientStatus.Approval
                    }
                    orange
                  />
                )}
              </Flex>
              <NormalText mt='0.75rem'>{purchaseRequest.title}</NormalText>
              <MediumText mb='0.75rem'>
                {purchaseRequest.department.name} • {purchaseRequest.requesterUser?.firstName}{' '}
                {purchaseRequest.requesterUser?.lastName}
              </MediumText>
              <Flex gap='0.5rem'>
                {totalAmount ? (
                  <>
                    <Flex column justcenter>
                      <SmallText colorVariant={ColorVariant.SECONDARY} uppercase mb='0.25rem'>
                        AMMOUNT
                      </SmallText>
                      <BigText mb='0.25rem'>$ {totalAmount}</BigText>
                    </Flex>
                    <DividerVertical />
                  </>
                ) : null}
                <Flex column justcenter>
                  <SmallText colorVariant={ColorVariant.SECONDARY} uppercase mb='0.25rem'>
                    ITEMS
                  </SmallText>
                  <BigText mb='0.25rem'>{purchaseRequest.items.length} line items</BigText>
                  {unresolvedFreeTextItems > 0 && (
                    <SmallText colorVariant={ColorVariant.WARNING}>
                      {unresolvedFreeTextItems} free-text unresolved free-text unresolved
                    </SmallText>
                  )}
                </Flex>
              </Flex>
            </Flex>
            {purchaseRequest.clientStatus === ProcurementRequestClientStatus.Draft && (
              <Button
                width='8rem'
                orange
                onClick={() => navigate(`/procurement/${purchaseRequest.id}/edit`)}
              >
                Edit Draft
              </Button>
            )}
            {purchaseRequest.currentWorkflowStep && purchaseRequest.canResolveWorkflowStep && (
              <Flex column justcenter gap='0.5rem'>
                {purchaseRequest.currentWorkflowStep?.type === WorkflowStepType.Approval && (
                  <>
                    <Button
                      width='8rem'
                      orange
                      onClick={() => setCommentModal(WorkflowResolutionStatus.Approved)}
                    >
                      Approve
                    </Button>
                    <OutlineButton
                      width='10rem'
                      colorVariant={ColorVariant.WARNING}
                      onClick={() => setCommentModal(WorkflowResolutionStatus.RequestChanges)}
                    >
                      Request Changes
                    </OutlineButton>
                    <OutlineButton
                      width='10rem'
                      colorVariant={ColorVariant.ERROR}
                      onClick={() => setCommentModal(WorkflowResolutionStatus.Rejected)}
                    >
                      Reject
                    </OutlineButton>
                  </>
                )}
              </Flex>
            )}
            {purchaseRequest.clientStatus === ProcurementRequestClientStatus.CreatePO && (
              <CreatePOButton request={purchaseRequest} />
            )}
          </Flex>
        </CardBody>
      </Card>
      {commentModal && (
        <ResolveWithCommentModal
          onClose={() => setCommentModal(null)}
          resolutionStatus={commentModal}
          handleRequest={(comment: string) => handleRequest(comment, commentModal)}
        />
      )}
    </>
  )
}
